import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2 } from "lucide-react";

interface AudioRecorderProps {
  onReady: (blob: Blob, nomeSugerido: string) => void;
  disabled?: boolean;
}

export function AudioRecorder({ onReady, disabled }: AudioRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => () => stopAll(), []);

  function stopAll() {
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    intervalRef.current = null;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  async function start() {
    setErro(null);
    setSeconds(0);
    chunksRef.current = [];
    try {
      // mediaDevices só existe em contexto seguro (HTTPS ou localhost)
      if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        const isSecure = typeof window !== "undefined" && (window.isSecureContext || ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname));
        setErro(isSecure
          ? "Seu navegador não suporta gravação de áudio."
          : "Gravação de áudio exige HTTPS. Acesse o site via https:// ou pelo tunnel.");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Tenta os mimes em ordem de preferência. iOS Safari só suporta mp4/aac.
      const candidatos = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const mime = candidatos.find((m) => {
        try { return (window as any).MediaRecorder?.isTypeSupported?.(m); } catch { return false; }
      }) || "";
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const finalMime = recorder.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: finalMime });
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const ext = finalMime.includes("ogg") ? ".ogg"
                  : finalMime.includes("mp4") || finalMime.includes("aac") ? ".m4a"
                  : ".webm";
        onReady(blob, `audio-${ts}${ext}`);
      };
      recorder.start();
      setIsRecording(true);
      intervalRef.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e: any) {
      setErro(e.message || "Não foi possível acessar o microfone");
      stopAll();
      setIsRecording(false);
    }
  }

  function stop() {
    setIsRecording(false);
    stopAll();
  }

  function cancel() {
    chunksRef.current = [];
    setIsRecording(false);
    stopAll();
    setSeconds(0);
  }

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="flex items-center gap-1">
      {!isRecording ? (
        <button
          type="button"
          onClick={start}
          disabled={disabled}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-50"
          title="Gravar áudio"
          aria-label="Gravar áudio"
        >
          <Mic size={18} />
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={stop}
            className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white animate-pulse"
            title="Parar e enviar"
            aria-label="Parar gravação"
          >
            <Square size={16} />
          </button>
          <span className="text-[11px] text-red-600 font-mono">
            {mm}:{ss}
          </span>
          <button
            type="button"
            onClick={cancel}
            className="p-1.5 text-slate-400 hover:text-red-500"
            title="Descartar"
            aria-label="Descartar gravação"
          >
            <Trash2 size={14} />
          </button>
        </>
      )}
      {erro && <span className="text-[10px] text-red-500">{erro}</span>}
    </div>
  );
}
